import { CircleX, Lock, RefreshCw, SearchX, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api-error';
import { cn } from '@/lib/utils';
import { EmptyState } from './empty-state';

/**
 * Esqueleto no formato do que vem: linhas para lista e tabela, bloco para cartão. Genérico de
 * propósito — um esqueleto sob medida por tela é o tipo de duplicação que o §24 proíbe.
 */
export function LoadingState({
  variant = 'rows',
  rows = 3,
  className,
}: {
  variant?: 'rows' | 'block';
  rows?: number;
  className?: string;
}) {
  return (
    <div role="status" aria-label="Carregando" className={cn('space-y-2', className)}>
      {variant === 'block' ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        Array.from({ length: rows }, (_, index) => <Skeleton key={index} className="h-10 w-full" />)
      )}
    </div>
  );
}

/**
 * "Não deu para saber" — nunca confundir com vazio (§29). Diz se foi a conexão ou o servidor,
 * mostra o código da requisição para o suporte achar no log e oferece tentar de novo.
 */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const api = error instanceof ApiError ? error : null;
  const network = api?.code === 'NETWORK_ERROR';
  return (
    <EmptyState
      className={className}
      icon={network ? WifiOff : CircleX}
      title={network ? 'Sem conexão com o servidor' : 'Não foi possível carregar'}
      description={
        <>
          {api?.message ?? 'Algo deu errado do nosso lado. Tente de novo em instantes.'}
          {api?.requestId && (
            <span className="mt-1 block text-xs">Código para o suporte: {api.requestId}</span>
          )}
        </>
      }
      action={
        onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw aria-hidden /> Tentar de novo
          </Button>
        )
      }
    />
  );
}

/** Sem permissão para ver. O backend negou; a tela só explica a quem pedir. */
export function PermissionDeniedState({ className }: { className?: string }) {
  return (
    <EmptyState
      className={className}
      icon={Lock}
      title="Você não tem acesso a esta informação"
      description="Peça a um administrador da empresa para liberar o seu perfil."
    />
  );
}

/** Registro que não existe ou está fora do seu escopo (a API responde 404 nos dois casos). */
export function NotFoundState({ title, className }: { title: string; className?: string }) {
  return (
    <EmptyState
      className={className}
      icon={SearchX}
      title={title}
      description="Pode não existir mais, ou o endereço está errado."
    />
  );
}
